import i18n from '@/lib/i18n';
import { manageError } from '@/lib/manage-error';
import { authService } from '@/service/AuthService';
import { useAuthStore } from '@/store/AuthStore';
import { useConferenceStore } from '@/store/ConferenceStore';
import intermind, { IntermindError } from 'intermind';
import { toast } from 'sonner';

const ROOM_ID_REGEX = /^[a-z]{3}-[a-z]{4}-[a-z]{3}$/;

export const setupIntermind = async (): Promise<void> => {
  await intermind.setup({
    apiPath: import.meta.env.VITE_APP_API_PATH,
    wsPath: import.meta.env.VITE_APP_WS_PATH,
    locale: i18n.language,
    apiToken: async () => {
      const user = await authService.getUser();
      const anonymousUser = await authService.getAnonymousUser();
      return user?.token || anonymousUser?.token || '';
    },
    onError: (error: IntermindError) => {
      if (error.statusCode === 401 && !error.url?.startsWith('/auth')) {
        authService.login();
      } else if (error.code === 'QuotaExceeded') {
        const user = useAuthStore.getState().user;
        const anonymousUser = useAuthStore.getState().anonymousUser;
        const quotaOwnerId = error.data?.quotaOwnerId;

        if (!quotaOwnerId || (quotaOwnerId !== user?.id && quotaOwnerId !== anonymousUser?.id)) {
          toast.error(i18n.t('errors.quotaExceeded.room'), {
            duration: Infinity,
          });
        } else {
          toast.error(error.message, {
            action: {
              label: i18n.t('errors.quotaExceeded.upgrade'),
              onClick: () => {
                window.open('/pricing', '_blank');
                toast.dismiss();
              },
            },
            duration: Infinity,
          });
        }
      } else {
        manageError(error, i18n.t('errors.intermindApiError'));
      }
    },
    customRequestHeaders: async () => {
      const user = useAuthStore.getState().user;
      const anonymousUser = useAuthStore.getState().anonymousUser;
      const roomIdStr = window.location.pathname.split('/')[1];
      const roomId = roomIdStr && ROOM_ID_REGEX.test(roomIdStr) ? roomIdStr : undefined;
      const conference = useConferenceStore.getState().conference;
      return {
        'X-Room-Id': roomId || conference?.roomId,
        'X-Conference-Id': conference?.id,
        'X-User-Id': user?.id || anonymousUser?.id,
      } as Record<string, string>;
    },
  });

  i18n.on('languageChanged', (lng) => {
    intermind.setLocale(lng);
  });
};
